import cn from 'classnames';
import throttle from 'lodash.throttle';
import { useTranslation } from 'next-i18next';
import { MouseEvent, useEffect, useRef, useCallback, useMemo } from 'react';
import { DynamicWidgets, SearchBox } from 'react-instantsearch';

import { Panel } from '@components/algolia';
import FacetRefinement from '@components/algolia/SearchResults/facets/FacetRefinement';
import { getFacetTitle } from '@components/algolia/SearchResults/facets/getFacetTitle';
import Button from '@components/ui/Button';
import IconWrapper from '@components/ui/Icons';

import { useEventListener } from '@hooks/useEventListener';
import useTabVisibility from '@hooks/useTabVisibility';

import { makeFacetTransformItems } from '@utils/algolia/facetOrdering';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import styles from './Sidebar.module.scss';

export { refinementListClassNames } from './refinementListClassNames';

const Sidebar = ({
  displayMobileNav,
  onCloseNavigation,
  locale,
  categoryUrl,
  priceAttribute,
}: {
  displayMobileNav: boolean;
  onCloseNavigation: () => void;
  locale?: string;
  categoryUrl: string;
  priceAttribute: string;
}) => {
  const { t } = useTranslation(['searchResults']);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const sideBarRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<Document | null>(null);

  useEffect(() => {
    documentRef.current = document;
  }, []);

  const handleOutsideClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!sideBarRef.current?.contains(e.target as Node)) {
      onCloseNavigation();
    }
  };

  const handleCloseButtonKeydown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onCloseNavigation();
      document
        .querySelector('#sort-by')
        ?.parentElement?.parentElement?.querySelector('button')
        ?.focus();
    }
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (displayMobileNav && e.key === 'Escape') {
      onCloseNavigation();
      document
        .querySelector('#sort-by')
        ?.parentElement?.parentElement?.querySelector('button')
        ?.focus();
    }
  };

  const handleSizeSelected = () => {
    onCloseNavigation();
  };

  let transitionTimeout: number | undefined;
  const throttledTransitionDelay = throttle(() => {
    // disabled transitions
    sideBarRef.current?.classList.add(styles.disableTransition);

    clearTimeout(transitionTimeout);

    transitionTimeout = window.setTimeout(() => {
      // enable transitions
      sideBarRef.current?.classList.remove(styles.disableTransition);
    }, 300);
  }, 300);

  useTabVisibility(sideBarRef);

  useEventListener('resize', throttledTransitionDelay);
  // For some reason, selecting a shoe width (on iOS chrome) is firing a rezise event
  // that closes the navigation. Commenting this out until we can figure out how to prevent that.
  // useEventListener('resize', throttledCloseNavigation);
  useEventListener('keydown', handleEscapeKey, documentRef);

  useEffect(() => {
    // assigns focus for accessibility and prevents scrolling in background
    if (displayMobileNav) {
      // opening
      closeButtonRef.current?.focus();
      document.documentElement.style.overflow = 'hidden';
    } else {
      // closing
      document.documentElement.style.overflow = 'auto';
    }
    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [displayMobileNav]);

  const FacetFallback = useCallback(
    ({ attribute }: { attribute: string }) => (
      <Panel title={getFacetTitle(attribute, t)} name={attribute}>
        <FacetRefinement attribute={attribute} locale={locale} />
      </Panel>
    ),
    [t, locale]
  );

  const transformWidgetItems = useMemo(
    () => makeFacetTransformItems({ categoryUrl, priceAttribute }),
    [categoryUrl, priceAttribute]
  );

  return (
    <div
      className={cn(styles.searchSidebarContainer, {
        [styles.searchSidebarContainerOpen]: displayMobileNav,
      })}
      onClick={handleOutsideClick}
    >
      <div className={styles.searchSidebar} ref={sideBarRef}>
        <button
          className={styles.mobileSidebarCloseButton}
          onClick={onCloseNavigation}
          onKeyDown={handleCloseButtonKeydown}
          aria-label={t('search.facets.close')}
          type="button"
          ref={closeButtonRef}
        >
          <IconWrapper icon="close" />
        </button>
        <div className={styles.searchSidebarInnerWrapper}>
          <DynamicWidgets
            facets={['*']}
            fallbackComponent={FacetFallback}
            maxValuesPerFacet={1000}
            transformItems={transformWidgetItems}
          >
            <Panel title={t('search.facet.category')} name="categories">
              <FacetRefinement attribute="categories.lvl0" locale={locale} />
            </Panel>

            <Panel title={t('search.facet.gender')} name={AlgoliaFacets.Gender}>
              <FacetRefinement
                attribute={AlgoliaFacets.Gender}
                locale={locale}
              />
            </Panel>

            <Panel title={t('search.facet.brand')} name="brand">
              <FacetRefinement
                attribute={AlgoliaFacets.Brand}
                locale={locale}
              />
            </Panel>

            <Panel title={t('search.facet.price')} name={priceAttribute}>
              <FacetRefinement attribute={priceAttribute} locale={locale} />
            </Panel>

            <Panel title={t('search.facet.colour')} name={AlgoliaFacets.Colour}>
              <FacetRefinement
                attribute={AlgoliaFacets.ColourVariants}
                locale={locale}
              />
            </Panel>

            <Panel title={t('search.facet.rating')} name={AlgoliaFacets.Rating}>
              <FacetRefinement
                attribute={AlgoliaFacets.ReviewScoreFlattened}
                locale={locale}
              />
            </Panel>

            <div id="sizeBucketRefinement">
              <Panel title={t('search.facet.size_bucketing')} name="size">
                <FacetRefinement
                  attribute="size"
                  locale={locale}
                  onRequestClose={handleSizeSelected}
                />
              </Panel>
            </div>

            <Panel
              title={t('search.facet.size.clothing.kids')}
              name={AlgoliaFacets.ClothingSizeKids}
            >
              <FacetRefinement
                attribute={AlgoliaFacets.ClothingSizeKids}
                locale={locale}
              />
            </Panel>
            <Panel
              title={t('search.facet.size.clothing.mens')}
              name={AlgoliaFacets.ClothingSizeMens}
            >
              <FacetRefinement
                attribute={AlgoliaFacets.ClothingSizeMens}
                locale={locale}
              />
            </Panel>
            <Panel
              title={t('search.facet.size.clothing.womens')}
              name={AlgoliaFacets.ClothingSizeWomens}
            >
              <FacetRefinement
                attribute={AlgoliaFacets.ClothingSizeWomens}
                locale={locale}
              />
            </Panel>
          </DynamicWidgets>

          <SearchBox
            classNames={{
              root: styles.searchBoxRoot,
            }}
          />

          <Button
            className={styles.mobileSidebarSeeAll}
            variant="secondary"
            label={t('search.facets.seeAll')}
            size="large"
            handleClick={onCloseNavigation}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
